import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RegrasCamera2DoItem from "./RegrasCamera2DoItem";
import CampoEditavel from "./CampoEditavel";
import CabecalhoSecaoRegras from "./CabecalhoSecaoRegras";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import palette from "../../common/styles/palette";
import { avaliarDeteccao, CORES, ROTULOS, rotuloContagemDeteccao } from "./detectabilidade";
import { InterviewCamera2Detection, InterviewItem, InterviewSkill, InterviewStep } from "../../types/Interview";

interface Props {
  enabled?: boolean;
  onToggle?: (enabled: boolean) => void;
  skills: InterviewSkill[];
  busy: boolean;
  versaoCampos?: number;
  onEditar: (item: InterviewItem, campo: keyof InterviewCamera2Detection, texto: string) => void;
  onPedirEnquadramento?: () => void;
  statusEnquadramento?: "carregando" | "pronta" | null;
  guidance?: string;
  onEditarGuidance?: (texto: string) => void;
  onEditarPassos?: (item: InterviewItem, passos: InterviewStep[]) => void;
  onEditarItem?: (item: InterviewItem, campo: "question" | "value", texto: string) => void;
  onAlternarCritico?: (item: InterviewItem) => void;
  onRemover?: (item: InterviewItem) => void;
  onPedirRegras?: (item: InterviewItem) => void;
  statusDoItem?: (item: InterviewItem) => "carregando" | "pronta" | null;
  onEditarSecao?: (skill: InterviewSkill, campo: "name" | "points", valor: string) => void;
  onAdicionarItem?: (skill: InterviewSkill) => void;
  onAdicionarSecao?: () => void;
  onRemoverSecao?: (skill: InterviewSkill) => void;
  secoesAbertas?: Record<string, boolean>;
  onAlterarSecoes?: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
}

export default function AbaRegrasCamera2({ enabled = false, onToggle, skills, busy, versaoCampos = 0, onEditar, onPedirEnquadramento, statusEnquadramento = null,
  guidance = "", onEditarGuidance, onEditarPassos, onEditarItem, onAlternarCritico,
  onRemover, onPedirRegras, statusDoItem, onEditarSecao, onAdicionarItem, onAdicionarSecao, onRemoverSecao,
  secoesAbertas, onAlterarSecoes,
}: Props) {
  const { t } = useTranslation();
  const [abertasLocais, setAbertasLocais] = useState<Record<string, boolean>>({});
  const abertas = secoesAbertas ?? abertasLocais;
  const setAbertas = onAlterarSecoes ?? setAbertasLocais;
  const [resumoAberto, setResumoAberto] = useState<string | null>(null);
  const [itemDestacado, setItemDestacado] = useState<{ ref: string } | null>(null);
  const itens = skills.flatMap((skill) => skill.items);
  const numeroPorItemRef = Object.fromEntries(itens.map((item, idx) => [item.ref, idx + 1])) as Record<string, number>;
  const regras = skills.flatMap((skill) => skill.items
    .filter((item) => Object.values(item.detection.camera2 ?? {}).some((valor) =>
      typeof valor === "string" ? valor.trim() !== "" : Array.isArray(valor) && valor.length > 0))
    .map((item) => ({ item, skill, deteccao: avaliarDeteccao({ ...item, detection: item.detection.camera2 ?? {} }, true) })));
  const comRegra = regras.length;
  const regrasNoResumo = resumoAberto === "todas" ? regras : regras.filter(({ skill }) => skill.ref === resumoAberto);

  useEffect(() => {
    if (!enabled || !itemDestacado) return;
    const timer = window.setTimeout(() => {
      const alvo = document.getElementById(`camera2-rule-item-${itemDestacado.ref}`);
      alvo?.scrollIntoView?.({ behavior: "auto", block: "start" });
      alvo?.focus({ preventScroll: true });
    }, 250);
    return () => window.clearTimeout(timer);
  }, [enabled, itemDestacado]);

  const navegarParaRegra = (skillRef: string, itemRef: string) => {
    setAbertas(Object.fromEntries(skills.map((skill) => [skill.ref, skill.ref === skillRef])));
    setItemDestacado({ ref: itemRef });
    setResumoAberto(null);
  };

  const tagRegras = (quantidade: number, total: number, resumo: string) => (
    <Chip size="small" variant="outlined" color={quantidade > 0 ? "success" : "default"}
      label={t("interview_camera2_rule_count", { count: quantidade, total })}
      disabled={!enabled || quantidade === 0} clickable={enabled && quantidade > 0}
      icon={resumoAberto === resumo ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
      aria-expanded={resumoAberto === resumo} aria-controls="camera2-detection-summary"
      onClick={() => setResumoAberto((atual) => atual === resumo ? null : resumo)} sx={{ fontWeight: 600 }} />
  );

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
      <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5, bgcolor: palette.surface, borderBottom: `1px solid ${palette.border}` }}>
        <Box sx={{ flex: "1 1 320px" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{t("interview_camera2_rules_title")}</Typography>
          <Typography variant="caption" sx={{ color: palette.textMuted }}>{t("interview_camera2_generation_help")}</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Chip size="small" variant="outlined" label={`${skills.length} ${t("interview_col_sections")}`} />
          {tagRegras(comRegra, itens.length, "todas")}
          {enabled && <Button size="small" variant="outlined" disabled={busy || !onToggle} onClick={() => onToggle?.(false)}>
            {t("interview_camera2_disable")}
          </Button>}
          {onAdicionarSecao && <Button size="small" variant="contained" disabled={busy || !enabled} onClick={onAdicionarSecao}>
            {t("interview_add_skill")}
          </Button>}
        </Box>
      </Box>
      <Box sx={{ position: "relative", flex: 1, display: "flex", minHeight: 0 }}>
      <Box component="fieldset" disabled={!enabled} aria-hidden={!enabled || undefined}
        {...(!enabled ? { inert: "" } : {})}
        sx={{ border: 0, m: 0, p: 0, minWidth: 0, flex: 1, display: "flex", flexDirection: "column", minHeight: 0,
          ...(!enabled && { pointerEvents: "none", userSelect: "none", filter: "blur(1.5px) saturate(0.85)", opacity: 0.42 }) }}>
      {resumoAberto && (
        <Paper id="camera2-detection-summary" role="region" aria-label={String(t("interview_detection_summary_title"))}
          elevation={0} sx={{ mx: 1.5, mt: 1.5, overflow: "hidden", border: `1px solid ${palette.border}`, borderRadius: 2, bgcolor: palette.surface }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 1.5, py: 1.25, bgcolor: palette.emeraldSoft }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{t("interview_camera2_rules_title")}</Typography>
            <Button size="small" onClick={() => setResumoAberto(null)}>{t("btn_close")}</Button>
          </Box>
          <Box sx={{ maxHeight: 260, overflowY: "auto" }}>
            {regrasNoResumo.map(({ item, skill, deteccao }) => (
              <Button key={item.ref} fullWidth onClick={() => navegarParaRegra(skill.ref, item.ref)}
                aria-label={`${t("interview_open_rule")} ${item.question}`}
                sx={{ display: "flex", justifyContent: "space-between", gap: 1.5, px: 1.5, py: 1.25, borderTop: `1px solid ${palette.border}`, borderRadius: 0, textAlign: "left", textTransform: "none" }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ color: palette.text, fontWeight: 600 }}>{item.question}</Typography>
                  <Typography variant="caption" sx={{ color: palette.textMuted }}>
                    {numeroPorItemRef[item.ref] ? `Item ${numeroPorItemRef[item.ref]} · ` : ""}{skill.name} · {deteccao.motivo}
                  </Typography>
                </Box>
                <Chip size="small" variant="outlined" color={CORES[deteccao.nivel]} label={ROTULOS[deteccao.nivel]} sx={{ flexShrink: 0 }} />
              </Button>
            ))}
          </Box>
        </Paper>
      )}
      <Box sx={{ flex: 1, overflowY: "auto", px: 1.5, py: 1.5 }}>
        {itens.length > 0 && (
          <Paper variant="outlined" role="region" aria-label={String(t("interview_camera2_guidance_title"))}
            sx={{ p: 2, mb: 2, borderColor: palette.brandSoft, bgcolor: palette.brandSoft + "40", borderRadius: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: palette.brandDark, display: "flex", gap: 0.75, alignItems: "center" }}>
                <VideocamOutlinedIcon fontSize="small" sx={{ color: palette.brand }} />
                {t("interview_camera2_guidance_title")}
              </Typography>
              {onPedirEnquadramento && <Button size="small" variant="outlined"
                disabled={busy && statusEnquadramento === null} onClick={onPedirEnquadramento}
                startIcon={<AutoAwesomeOutlinedIcon fontSize="small" />}
                sx={{ borderColor: palette.brandSoft, color: palette.brand, bgcolor: palette.brandSoft + "40",
                  fontWeight: 600, textTransform: "none", "&:hover": { bgcolor: palette.brandSoft } }}>
                {statusEnquadramento === "carregando" ? "ANÁLISE EM ANDAMENTO"
                  : statusEnquadramento === "pronta" ? "Ver regras sugeridas" : t("interview_ask_assistant")}
              </Button>}
            </Box>
            <Typography variant="caption" sx={{ display: "block", mb: 1, color: palette.textMuted }}>
              {t("interview_camera2_guidance_help")}
            </Typography>
            <CampoEditavel key={`guidance:${versaoCampos}`} label={String(t("interview_camera2_guidance_label"))}
              valor={guidance} multiline minRows={2} fullWidth disabled={busy}
              onComitar={(texto) => onEditarGuidance?.(texto)} />
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, mt: 1 }}>
              <InfoOutlinedIcon sx={{ fontSize: 16, color: palette.info, flexShrink: 0, mt: "1px" }} />
              <Typography component="p" variant="caption" sx={{ m: 0, fontSize: 12, lineHeight: 1.5, color: palette.textMuted }}>
                {t("interview_camera2_help")}
              </Typography>
            </Box>
          </Paper>
        )}
        {itens.length === 0 && <Typography color="text.secondary">{t("interview_camera2_empty")}</Typography>}
        {skills.map((skill) => {
          const aberta = abertas[skill.ref] ?? skill.items.length === 0;
          const regrasDaSecao = regras.filter((regra) => regra.skill.ref === skill.ref);
          const revisar = regrasDaSecao.filter((regra) => regra.deteccao.nivel !== "forte").length;
          const revisarCameraGeral = skill.items.filter((item) => avaliarDeteccao(item).nivel !== "forte").length;
          const primeiroItemDoStatus = (revisar > 0
            ? regrasDaSecao.find((regra) => regra.deteccao.nivel !== "forte")
            : regrasDaSecao[0]) ?? null;
          return (
            <Paper key={skill.ref} elevation={0} sx={{ mb: 2.5, overflow: "hidden", borderRadius: 2.5,
              border: `1px solid ${palette.border}`, bgcolor: palette.background }}>
              <CabecalhoSecaoRegras skill={skill} busy={busy} aberta={aberta} idConteudo={`camera2-section-${skill.ref}`}
                onAlternar={() => setAbertas((atuais) => ({ ...atuais, [skill.ref]: !aberta }))}
                onEditar={(secao, campo, valor) => onEditarSecao?.(secao, campo, valor)}
                rotuloStatus={primeiroItemDoStatus ? rotuloContagemDeteccao(revisar || regrasDaSecao.length, revisar > 0 ? "revisar" : "forte") : undefined}
                rotuloStatusReferencia={skill.items.length > 0
                  ? rotuloContagemDeteccao(revisarCameraGeral || skill.items.length, revisarCameraGeral > 0 ? "revisar" : "forte")
                  : undefined}
                revisar={revisar > 0}
                onAbrirStatus={primeiroItemDoStatus ? () => navegarParaRegra(skill.ref, primeiroItemDoStatus.item.ref) : undefined}
                onAdicionarItem={onAdicionarItem ? () => onAdicionarItem(skill) : undefined}
                onRemover={onRemoverSecao ? () => onRemoverSecao(skill) : undefined} />
              <Collapse in={aberta} timeout={200} unmountOnExit id={`camera2-section-${skill.ref}`}>
                <Divider />
                <Box sx={{ p: 1.5 }}>
                  {skill.items.length === 0 && (
                    <Box sx={{ p: 2, textAlign: "center", color: palette.textMuted }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>{t("interview_empty_section_rules")}</Typography>
                      {onAdicionarItem && <Button size="small" variant="outlined" disabled={busy}
                        onClick={() => onAdicionarItem(skill)}>{t("interview_add_item")}</Button>}
                    </Box>
                  )}
                  {skill.items.map((item) => (
                    <Box key={item.ref} id={`camera2-rule-item-${item.ref}`} tabIndex={-1}
                      role="region" aria-label={`${t("interview_open_rule")} ${item.question}`}
                      sx={{ scrollMarginTop: 16, outline: "none", borderRadius: 2, boxShadow: itemDestacado?.ref === item.ref ? `0 0 0 2px ${palette.brandSoft}` : "none" }}>
                      <RegrasCamera2DoItem item={item} numero={numeroPorItemRef[item.ref]} busy={busy} versaoCampos={versaoCampos}
                        onEditar={onEditar} onEditarPassos={onEditarPassos} onEditarItem={onEditarItem}
                        onAlternarCritico={onAlternarCritico} onRemover={onRemover}
                        onPedirRegras={onPedirRegras} sugestaoStatus={statusDoItem?.(item)} />
                    </Box>
                  ))}
                </Box>
              </Collapse>
            </Paper>
          );
        })}
      </Box>
      </Box>
      {!enabled && <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", p: 3 }}>
        <Paper elevation={0} sx={{ maxWidth: 440, p: 3, textAlign: "center", border: `1px solid ${palette.border}`, borderRadius: 2, bgcolor: palette.surface }}>
          <LockOutlinedIcon sx={{ color: palette.brand, mb: 1 }} />
          <Typography variant="body2" sx={{ color: palette.textMuted, mb: 2 }}>{t("interview_camera2_disabled_help")}</Typography>
          <Button variant="contained" disabled={busy || !onToggle} startIcon={<VideocamOutlinedIcon />} onClick={() => onToggle?.(true)}>
            {t("interview_camera2_enable")}
          </Button>
        </Paper>
      </Box>}
      </Box>
    </Box>
  );
}
